import { booleanAttribute, Component, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { GalaxyButtonGroupModule } from '@vendasta/galaxy/button-group';

interface ToneDetails {
  translationKey: string;
  name: string;
  icon: string;
}

export enum UpdateOption {
  UNSPECIFIED = 0,
  IMPROVE_WRITING = 1,
  FIX_SPELLING_AND_GRAMMAR = 2,
  MAKE_SHORTER = 3,
  OPTIMIZE_BY_KEYWORDS = 4,
  CHANGE_TONE = 5,
}

export interface UpdateRequest {
  existingValue: string;
  option: UpdateOption;
  tone?: ToneDetails;
}

export const ToneMap: Record<string, ToneDetails> = {
  FRIENDLY: { translationKey: 'GALAXY.AI_TEXT_BUTTON.TONE.FRIENDLY', name: 'friendly', icon: '😊' },
  LUXURIOUS: { translationKey: 'GALAXY.AI_TEXT_BUTTON.TONE.LUXURIOUS', name: 'luxurious', icon: '💎' },
  PROFESSIONAL: { translationKey: 'GALAXY.AI_TEXT_BUTTON.TONE.PROFESSIONAL', name: 'professional', icon: '👔' },
  RELAXED: { translationKey: 'GALAXY.AI_TEXT_BUTTON.TONE.RELAXED', name: 'relaxed', icon: '🧘‍' },
  BOLD: { translationKey: 'GALAXY.AI_TEXT_BUTTON.TONE.BOLD', name: 'bold', icon: '💪' },
  ADVENTUROUS: { translationKey: 'GALAXY.AI_TEXT_BUTTON.TONE.ADVENTUROUS', name: 'adventurous', icon: '🌍' },
  WITTY: { translationKey: 'GALAXY.AI_TEXT_BUTTON.TONE.WITTY', name: 'witty', icon: '🤔' },
  PERSUASIVE: { translationKey: 'GALAXY.AI_TEXT_BUTTON.TONE.PERSUASIVE', name: 'persuasive', icon: '💬' },
  EMPATHETIC: { translationKey: 'GALAXY.AI_TEXT_BUTTON.TONE.EMPATHETIC', name: 'empathetic', icon: '❤' },
};

@Component({
  selector: 'glxy-ai-text-button',
  templateUrl: './ai-text-button.component.html',
  styleUrls: ['./ai-text-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatButtonModule, GalaxyButtonGroupModule, MatIconModule, MatMenuModule, TranslateModule],
})
export class AiTextButtonComponent {
  value = input('');
  fieldName = input('');
  disabled = input(false, { transform: booleanAttribute });

  suggest = output<void>();
  update = output<UpdateRequest>();

  tones = Array.from(Object.values(ToneMap));

  // Register the galaxy-ai-icon with the icon registry.
  galaxyAiIconService = inject(GalaxyAiIconService);

  suggestUpdate(option: UpdateOption, tone?: ToneDetails): void {
    this.update.emit({ existingValue: this.value(), option: option, tone: tone });
  }

  protected readonly UpdateOption = UpdateOption;
}
